import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/lib/supabase";

export interface SystemMetric {
  id: number;
  metric_date: string;
  activations: number;
  licenses_issued: number;
  api_requests: number;
  uptime: number;
}

export interface MonitorStats {
  total_licenses: number;
  active_licenses: number;
  expired_licenses: number;
  suspended_licenses: number;
  revoked_licenses: number;
  online_seats: number;
  total_customers: number;
  updated_at: string | null;
}

interface UseMonitorDataResult {
  metrics: SystemMetric[];
  stats: MonitorStats | null;
  loading: boolean;
  error: string | null;
  reload: () => void;
}

export function useMonitorData(): UseMonitorDataResult {
  const [metrics, setMetrics] = useState<SystemMetric[]>([]);
  const [stats, setStats] = useState<MonitorStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [metricsRes, statsRes] = await Promise.all([
        supabase.from("system_metrics").select("*").order("metric_date", { ascending: true }).limit(60),
        supabase.from("platform_stats").select("*").eq("id", 1).maybeSingle(),
      ]);
      if (metricsRes.error) throw metricsRes.error;
      if (statsRes.error) throw statsRes.error;
      setMetrics((metricsRes.data as SystemMetric[]) ?? []);
      setStats((statsRes.data as MonitorStats | null) ?? null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "监控数据加载失败，请稍后重试");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  return { metrics, stats, loading, error, reload: load };
}